'use client';

import { type ReactNode, useState } from 'react';
import { useProductLinkNavigation } from '../../../product/hooks/useProductLinkNavigation';
import { useBrandStoreNavigation } from '../../hooks/useBrandStoreNavigation';
import { BrandMessageInput } from './common/BrandMessageInput';
import { CampaignDetailNotice } from './common/CampaignDetailNotice';
import { CampaignInfo } from './common/CampaignInfo';
import { ContentGuide } from './common/ContentGuide';
import { ContentSubmissionInput } from './common/ContentSubmissionInput';
import { ContentTypeSection } from './common/ContentTypeSection';
import { ProductCard } from './common/ProductCard';
import {
  groupProductCards,
  ProductCardGroup,
  ProductCardSectionTitle,
} from './common/ProductCardGroup';
import { SeedingCampaignDetailLayout } from './common/SeedingCampaignDetailLayout';
import { SeedingShippingSection } from './common/SeedingShippingSection';
import { ShippingTrackingCard } from './common/ShippingTrackingCard';
import { useCountdown } from './hooks/useCountdown';
import { ProposeAcceptRejectActionBar } from './propose/ProposeAcceptRejectActionBar';
import { ProposeSoldOutActionBar } from './propose/ProposeSoldOutActionBar';
import { CountdownHeader } from './recruit/CountdownHeader';
import { PROGRESS_TITLE_BY_STATUS } from './recruit/progressTitle';
import { SectionDivider } from './shared/SectionDivider';
import {
  PROPOSE_STATUSES_WITH_COUNTDOWN_HEADER,
  PROPOSE_STATUSES_WITH_EDITABLE_SHIPPING_ADDRESS,
  PROPOSE_STATUSES_WITH_GUIDE_INFO_ICON,
  PROPOSE_STATUSES_WITH_INFO_CARD,
  PROPOSE_STATUSES_WITH_PROGRESS_HEADER,
  shouldShowContentGuideSheet,
} from './shared/visibility';
import type {
  SeedingCampaignDetail,
  SeedingContentType,
  SeedingCtaAction,
  SeedingProposeStatus,
  SeedingSnsChannel,
} from './types';

interface SeedingProposeDetailPageViewProps {
  campaignId: number;
  detail: SeedingCampaignDetail;
  /** CTA 액션 핸들러 (라우터/API 연동은 호출 측에서 주입) */
  onCtaAction?: (action: SeedingCtaAction) => void;
  /** 옵션 선택 클릭 */
  onSelectProductOption?: (productId: number) => void;
  /** 배송지 변경 클릭 */
  onChangeAddress?: () => void;
  /** 콘텐츠 제출 항목 클릭 — content-submission 라우트 query 로 전달되는 채널 식별자 */
  onSubmitContent?: (channel: SeedingSnsChannel) => void;
  /** 콘텐츠 가이드 정보 아이콘 클릭 — 콘텐츠 등록 기간 안내 페이지로 진입 */
  onShowContentPeriod?: () => void;
  /** 콘텐츠 제작 가이드 시트 열기 — preparing 단계에서 사용 */
  onShowContentGuide?: () => void;
  /**
   * 콘텐츠 유형(editable) 선택 변경 — 수락 payload 에 반영하기 위해 호출 측으로 올린다.
   * 선택값은 API enum(`SeedingContentType`), 미선택은 null.
   */
  onSelectContentType?: (type: SeedingContentType | null) => void;
  /** 브랜드 전달 메시지(free-text) 변경 — draft store 보존을 위해 호출 측으로 올린다. (SID-4157) */
  onChangeBrandMessage?: (message: string) => void;
  /**
   * 약관 동의 — 수락 가능(dual CTA) 상태에서만 전달.
   * `section`: 최하단 동의 섹션 UI. 제안형은 버튼 비활성 대신 클릭 시 다이얼로그로 게이트하므로
   * `primaryCtaDisabled` 는 사용하지 않는다.
   */
  agreement?: { section: ReactNode };
  /** 상품 썸네일 비율 — 29CM=square(5:5) / 무신사=portrait(세로). (SID-4424) */
  imageRatio?: 'square' | 'portrait';
  /** 수락 mutation pending — 수락 CTA loading + 거절 CTA 비활성. 더블탭 방지. (SID-4740) */
  acceptLoading?: boolean;
  /** 거절 mutation pending — 수락 CTA 비활성. 거절 시트를 닫아도 거절 처리 중엔 수락 차단. (SID-4740) */
  rejectPending?: boolean;
  className?: string;
}

export const SeedingProposeDetailPageView = ({
  campaignId: _campaignId,
  detail,
  onCtaAction,
  onSelectProductOption,
  onChangeAddress,
  onSubmitContent,
  onShowContentPeriod,
  onShowContentGuide,
  onSelectContentType,
  onChangeBrandMessage,
  agreement,
  imageRatio = 'portrait',
  acceptLoading,
  rejectPending,
  className,
}: SeedingProposeDetailPageViewProps) => {
  const [contentTypeSelection, setContentTypeSelection] = useState<SeedingContentType | null>(() =>
    detail.contentType.mode === 'editable' ? detail.contentType.selectedType : null,
  );
  const [brandMessage, setBrandMessage] = useState<string>(detail.contentType.message);

  // 상품 카드 브랜드명 클릭 → 브랜드 스토어 홈(새 웹뷰). 시딩=단일 브랜드라 캠페인 brandId 사용. (QAT-39448)
  const goToBrandStore = useBrandStoreNavigation();
  const brandId = detail.brand.brandId;
  const handleBrandClick = brandId ? () => goToBrandStore(brandId) : undefined;
  const navigateToProduct = useProductLinkNavigation();

  const status = detail.status as SeedingProposeStatus;

  const showCountdownHeader =
    !!detail.countdown && PROPOSE_STATUSES_WITH_COUNTDOWN_HEADER.includes(status);
  const showProgressHeader = PROPOSE_STATUSES_WITH_PROGRESS_HEADER.includes(status);

  const showInfoCard = PROPOSE_STATUSES_WITH_INFO_CARD.includes(status);

  // 거절(종료)은 읽기전용 — 콘텐츠 유형 선택/표시 섹션 미노출. 대신 콘텐츠 유형은
  // '콘텐츠 제작 가이드' 안에 읽기전용 행으로 노출 (Figma 2276-34571). (SID-4210)
  const showContentTypeSection = status !== 'rejected';
  const rejectedContentTypes =
    status === 'rejected' && detail.contentType.mode === 'editable'
      ? detail.contentType.options.map((option) => option.label).join(', ')
      : null;

  // F14-9 — preparing 단계 등에서만 배송지 변경 가능. mock 의 editable 도 함께 봐서 AND.
  const showShippingAddressChange =
    detail.shippingAddress.editable &&
    PROPOSE_STATUSES_WITH_EDITABLE_SHIPPING_ADDRESS.includes(status);

  const isEditableContentType = detail.contentType.mode === 'editable';
  const showBrandMessage =
    isEditableContentType && (status === 'pending' || status === 'productSelected');

  const showContentSubmission = !!detail.contentSubmission;
  const showShippingTracking = !!detail.shippingTracking;
  const showFab = status !== 'soldOut';

  const handleProductAction = (productId: number) => {
    onSelectProductOption?.(productId);
  };

  // 수락 마감(responseDeadlineAt = countdown.targetAt) 경과 시 수락 CTA 비활성. 거절은 유지. (QAT-39695)
  const isAcceptDeadlinePassed =
    useCountdown({ targetAt: detail.countdown?.targetAt ?? null })?.isExpired ?? false;

  const renderCta = () => {
    if (!detail.primaryCta) return null;
    if (detail.primaryCta.mode === 'dual') {
      // 응답 기한 경과 시 수락·거절 **둘 다** 클라에서 선제 비활성. 마감된 제안은 두 액션 모두 무효.
      // (서버 409 SEEDING-001-007 failback 은 클라 카운트다운이 아직 갱신 안 된 race 의 안전망으로 유지.)
      // (QAT-39695 수락 비활성 + 거절 비활성 확장 / CMQA-7164 failback)
      const gateByDeadline = isAcceptDeadlinePassed;
      const cta = gateByDeadline
        ? {
            ...detail.primaryCta,
            primary: { ...detail.primaryCta.primary, disabled: true },
            secondary: { ...detail.primaryCta.secondary, disabled: true },
          }
        : detail.primaryCta;
      return (
        <ProposeAcceptRejectActionBar
          cta={cta}
          acceptLoading={acceptLoading}
          rejectPending={rejectPending}
          onAccept={() => onCtaAction?.(detail.primaryCta?.primary.action ?? 'ACCEPT')}
          onReject={() =>
            onCtaAction?.(
              detail.primaryCta?.mode === 'dual' ? detail.primaryCta.secondary.action : 'REJECT',
            )
          }
        />
      );
    }
    return (
      <ProposeSoldOutActionBar
        cta={detail.primaryCta}
        onAction={() => onCtaAction?.(detail.primaryCta?.primary.action ?? 'GO_BACK')}
      />
    );
  };

  return (
    <SeedingCampaignDetailLayout
      className={className}
      showScrollToTop={showFab}
      floatingCta={renderCta()}
    >
      {(showProgressHeader || showCountdownHeader || showInfoCard) && (
        <CampaignInfo.Container firstSection>
          {showProgressHeader && PROGRESS_TITLE_BY_STATUS[status] && (
            <CampaignInfo.Title>{PROGRESS_TITLE_BY_STATUS[status]}</CampaignInfo.Title>
          )}
          {showCountdownHeader && detail.countdown && (
            <CountdownHeader
              label={detail.countdown.label}
              targetAt={detail.countdown.targetAt}
              expiredLabel={detail.countdown.expiredLabel}
            />
          )}
          {showInfoCard && (
            <>
              <CampaignInfo.Card
                title={detail.campaignInfo.title}
                description={detail.campaignInfo.description}
              />
              <CampaignInfo.Table rows={detail.campaignInfo.infoRows} labelWidth={80} />
            </>
          )}
        </CampaignInfo.Container>
      )}

      {showContentSubmission && detail.contentSubmission && (
        <>
          <SectionDivider />
          <ContentSubmissionInput
            isComplete={detail.contentSubmission.isComplete}
            items={detail.contentSubmission.items.map((item) => ({
              ...item,
              onAction: (channel) => onSubmitContent?.(channel),
            }))}
          />
        </>
      )}

      <SectionDivider />

      {/* 바깥 Container = 그룹 간 간격(gap-y) 담당. pt-12 로 divider 기준 위 24px 정합. (SID-4329) */}
      <CampaignInfo.Container>
        <ProductCardSectionTitle
          count={detail.products.length}
          guideText={detail.productSectionGuide ?? undefined}
          guideTextEmphasis={detail.productSectionGuideEmphasis ?? 'default'}
        />
        {groupProductCards(detail.products).map((group, groupIndex) => (
          <ProductCardGroup.Container key={`${group.category}-${groupIndex}`}>
            {group.category && (
              <ProductCardGroup.Title soldOut={group.items.every((item) => item.soldOut)}>
                {group.category}
              </ProductCardGroup.Title>
            )}
            <ProductCardGroup.List>
              {group.items.map((product) => (
                <ProductCard key={product.productId} soldOut={product.soldOut}>
                  <ProductCard.ImageInfoRow>
                    <ProductCard.Image
                      src={product.imageUrl}
                      alt={product.name}
                      soldOut={product.soldOut}
                      imageRatio={imageRatio}
                      onClick={() => navigateToProduct(product.webUrl)}
                    />
                    <ProductCard.Info
                      brand={product.brand}
                      name={product.name}
                      selectedOption={product.selectedOption ?? undefined}
                      price={product.price}
                      soldOut={product.soldOut}
                      onBrandClick={handleBrandClick}
                    />
                  </ProductCard.ImageInfoRow>
                  {product.actionLabel && !product.soldOut && (
                    <ProductCard.Action onClick={() => handleProductAction(product.productId)}>
                      {product.actionLabel}
                    </ProductCard.Action>
                  )}
                </ProductCard>
              ))}
            </ProductCardGroup.List>
          </ProductCardGroup.Container>
        ))}
      </CampaignInfo.Container>

      {/* 거절(종료)은 콘텐츠 유형 섹션이 빠지므로 그 앞 divider 도 함께 숨겨 중복 방지. */}
      {showContentTypeSection && <SectionDivider />}

      {showContentTypeSection && isEditableContentType && (
        <ContentTypeSection
          mode="radio"
          options={
            detail.contentType.mode === 'editable'
              ? detail.contentType.options.map((option) => ({
                  type: option.value,
                  label: option.label,
                  deadline: option.deadline,
                }))
              : []
          }
          selectedType={contentTypeSelection}
          onSelect={(type) => {
            setContentTypeSelection(type);
            onSelectContentType?.(type);
          }}
          footer={
            showBrandMessage ? (
              // 브랜드 메시지 = 콘텐츠 유형 섹션의 형제 아이템 (라디오 직후 gap-8). (SID-4139/SID-4141)
              <BrandMessageInput
                embedded
                title="어떻게 스타일링을 할 예정인가요? (선택)"
                value={brandMessage}
                onChange={(value) => {
                  setBrandMessage(value);
                  // SNS 채널 등록 등 라우트 왕복 간 보존을 위해 draft store 로 끌어올린다. (SID-4157)
                  onChangeBrandMessage?.(value);
                }}
                placeholder={
                  detail.contentType.mode === 'editable'
                    ? detail.contentType.messagePlaceholder
                    : undefined
                }
                maxLength={
                  detail.contentType.mode === 'editable' ? detail.contentType.messageMaxLength : 200
                }
              />
            ) : undefined
          }
        />
      )}

      {showContentTypeSection && !isEditableContentType && detail.contentType.mode === 'frozen' && (
        <ContentTypeSection
          mode="frozen-detail"
          snsChannel={detail.contentType.selectedSnsChannelLabel ?? '-'}
          contentTypeLabel={detail.contentType.selectedTypeLabel}
          uploadDeadlineNotice={detail.contentType.uploadDeadlineNotice ?? '-'}
          brandMessage={detail.contentType.message || undefined}
          // 수락 완료(CONFIRMED) 후 읽기전용으로 보여줄 때도 제안형 타이틀 유지 — PENDING editable
          // 과 동일 문구로 Figma(node 1-24185) 정합. (SID-4150 / SID-4141 연동)
          brandMessageTitle="어떻게 스타일링을 할 예정인가요? (선택)"
        />
      )}

      <SectionDivider />

      {/* pt-12 — divider 다음 섹션이므로 모집형과 동일하게 24px 정합. (SID-4329, default pt-24 → 36 방지) */}
      <ContentGuide.Container>
        <ContentGuide.Title
          // info ⓘ 아이콘 — 수락 완료(preparing) 이후 상세부터 활성. (SID-4150)
          // preparing 은 가이드 시트, 이후 진행 단계는 콘텐츠 등록 기간 안내(둘 다 동일 시트 진입).
          onInfoClick={
            PROPOSE_STATUSES_WITH_GUIDE_INFO_ICON.includes(status)
              ? shouldShowContentGuideSheet('propose', status)
                ? onShowContentGuide
                : onShowContentPeriod
              : undefined
          }
        >
          콘텐츠 제작 가이드
        </ContentGuide.Title>
        {/* 거절(종료) 읽기전용 — 콘텐츠 유형(허용 유형 목록)을 가이드 첫 행으로 노출. (Figma 2276-34571) */}
        {rejectedContentTypes && (
          <ContentGuide.Subsection label="콘텐츠 유형" body={rejectedContentTypes} />
        )}
        {/* 필수 해시태그 Subsection 내부 gap-y 8px. (모집형 PageView 와 동일) */}
        <ContentGuide.Subsection
          label="필수 해시태그"
          subtitle={detail.contentGuide.hashtagNotice}
          className="gap-[8px]!"
        >
          <ContentGuide.HashtagBox text={detail.contentGuide.hashtagText} />
        </ContentGuide.Subsection>
        <ContentGuide.Subsection label="브랜드 요청사항" body={detail.contentGuide.brandRequest} />
        {/* 제안 번호 — 수락 후(preparing~) 노출. 값은 participation.id (모집형 '신청 번호'와 동일). (SID-4331) */}
        {detail.selectionNumber && (
          <ContentGuide.Subsection label="제안 번호" body={detail.selectionNumber} />
        )}
      </ContentGuide.Container>

      {showShippingTracking && detail.shippingTracking && (
        <>
          <SectionDivider />
          <ShippingTrackingCard
            statusLabel={detail.shippingTracking.statusLabel}
            labelVariant={detail.shippingTracking.labelVariant}
            banners={detail.shippingTracking.banners}
            details={detail.shippingTracking.details}
          />
        </>
      )}

      <SectionDivider />

      <SeedingShippingSection
        address={detail.shippingAddress}
        changeable={showShippingAddressChange}
        onChangeAddress={onChangeAddress}
      />

      {/* 디자인 요청(순서 변경): 약관 동의 → 유의사항. 약관 동의는 자체 상단 SectionDivider 로 경계를
          만들고(없으면 아래 divider 가 유의사항 경계), 유의사항이 항상 마지막이라 하단 safe-area(pb-72)는
          유의사항(CampaignDetailNotice 자체 className)이 책임. */}
      {agreement?.section}

      <SectionDivider />

      <CampaignDetailNotice />
    </SeedingCampaignDetailLayout>
  );
};
